import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Text,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Heading,
  VStack,
  Spinner,
  SimpleGrid,
  Divider,
  Button,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from "@chakra-ui/react";
import { SearchIcon } from "@chakra-ui/icons";
import { constants } from "constants/constants";

const API_URL = constants.apiUrl;

export default function ViewAmazonDFOrder() {
  const getQueryParams = (url) => {
    const queryString = url.split("?")[1];
    if (!queryString) return {};
    const paramsArray = queryString.split("&");
    const params = {};
    paramsArray.forEach((param) => {
      const [key, value] = param.split("=");
      params[key] = decodeURIComponent(value);
    });
    return params;
  };

  const queryParams = getQueryParams(window.location.href);
  const JiwaOrderNo = queryParams["JiwaOrderNo"] || "";
  const AmazonInvoiceNo = queryParams["AmazonInvoiceNo"] || "";

  const [orderData, setOrderData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalData, setModalData] = useState([]);
  const [selectedBarcode, setSelectedBarcode] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();

  // 🔹 Fetch order data
  useEffect(() => {
    const fetchOrderData = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const response = await axios.get(`${API_URL}/get-amazon-dforders`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { JiwaOrderNo, AmazonInvoiceNo, Days: 7 },
          withCredentials: true,
        });

        const records = response.data.records;
        const order = Array.isArray(records) ? records[0] : records;
        setOrderData(order || null);
      } catch (err) {
        console.error("Failed to fetch order data:", err);
        setError("Failed to load order details.");
      } finally {
        setIsLoading(false);
      }
    };

    if (JiwaOrderNo || AmazonInvoiceNo) fetchOrderData();
  }, [JiwaOrderNo, AmazonInvoiceNo]);

  // 🔹 Determine problematic rows
  const hasPriceIssue = (line) =>
    Number(line.AmzPrice) < Number(line.DkaPrice);

  const hasQtyMismatch = (line) =>
    Number(line.AmzQuantity) !== Number(line.DkaQuantity);

  const hasAnyPriceIssue = orderData?.OrderLines?.some(hasPriceIssue);
  const hasAnyQtyMismatch = orderData?.OrderLines?.some(hasQtyMismatch);

  const isApprovable = !hasAnyPriceIssue && !hasAnyQtyMismatch;

  const renderDetail = (label, value) => (
    <Text fontSize="md">
      <Text as="span" fontWeight="bold" mr={2}>
        {label}:
      </Text>
      <Text as="span">{value}</Text>
    </Text>
  );

  // 🔹 Handle Approve / Reject actions
  const handleOrderAction = async (action) => {
  console.log("Button clicked:", action); // 👈 Add this

  try {
    const token = localStorage.getItem("accessToken");
    console.log("Token:", token); // 👈 See if token is null/undefined

    await axios.post(
      `${API_URL}/action-amazon-dforder`,
      { AmazonInvoiceNo, OrderAction: action },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    alert(`Order ${action} successfully!`);
  } catch (error) {
    console.error(`Failed to ${action} order:`, error);
    alert(`Failed to ${action} order.`);
  }
};


  // 🔹 Modal for missing PartNo
  const handleFindPart = async (barcode) => {
    setSelectedBarcode(barcode);
    onOpen();
    try {
      const token = localStorage.getItem("accessToken");
      const response = await axios.get(`${API_URL}/find-part-by-barcode`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { barcode },
      });
      setModalData(response.data.results || []);
    } catch (err) {
      console.error("Failed to fetch parts:", err);
      setModalData([]);
    }
  };

  return (
    <Flex direction="column" align="center" justify="center" minHeight="100vh" p={{ base: "4", md: "8" }} bg="gray.50">
      <Box bg="white" p={{ base: "4", md: "8" }} borderRadius="lg" boxShadow="xl" w="100%" maxW="1200px">
        <Heading size="xl" mb={6} textAlign="center" color="gray.700">
          Order Details
        </Heading>
        <Divider mb={6} />

        {isLoading ? (
          <Flex height="200px" justify="center" align="center">
            <Spinner size="xl" />
          </Flex>
        ) : error ? (
          <Text color="red.500" textAlign="center">{error}</Text>
        ) : !orderData ? (
          <Text color="gray.500" textAlign="center">No order data found.</Text>
        ) : (
          <VStack spacing={8} align="stretch">
            {/* Order Summary */}
            <Box bg="blue.50" p={6} borderRadius="lg">
              <Heading size="md" mb={4} color="blue.700">Order Summary</Heading>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {renderDetail("Amazon Order No", orderData.AmazonOrderNo)}
                {renderDetail("Jiwa Order No", orderData.JiwaOrderNo)}
                {renderDetail("Amazon Order Type", orderData.AmazonOrderType)}
                {renderDetail("Order Date", orderData.OrderDate)}
                {renderDetail("Status", orderData.OrderStatus)}
                {renderDetail("Amazon Total", `$${Number(orderData.AmazonOrderTotal || 0).toFixed(2)}`)}
                {renderDetail("Davka Total", `$${Number(orderData.DavkaOrderTotal || 0).toFixed(2)}`)}
              </SimpleGrid>
            </Box>

            <Divider />

            {/* Table */}
            <Box w="100%" border="1px" borderColor="gray.200" borderRadius="lg">
              <Table variant="simple" size="sm" width="100%">
                <Thead bg="gray.100">
                  <Tr>
                    <Th>Part No</Th>
                    <Th>Product Name</Th>
                    <Th>ASIN</Th>
                    <Th>Barcode</Th>
                    <Th isNumeric>Amz Price</Th>
                    <Th isNumeric>Dka Price</Th>
                    <Th isNumeric>Amz Qty</Th>
                    <Th isNumeric>Dka Qty</Th>
                    <Th isNumeric width="7%">SOH</Th>
                    
                  </Tr>
                </Thead>

                <Tbody>
                  {Array.isArray(orderData.OrderLines) && orderData.OrderLines.length > 0 ? (
                    orderData.OrderLines.map((line, index) => {
                      // 🔹 Determine row color logic
                      const priceIssue = hasPriceIssue(line);
                      const qtyIssue = hasQtyMismatch(line);

                      const rowBg = priceIssue || qtyIssue ? "red.200" : "transparent";

                      return (
                        <Tr key={index} bg={rowBg}>
                          <Td fontSize="sm">
                            {line.PartNo ? (
                              line.PartNo
                            ) : (
                              <IconButton
                                icon={<SearchIcon />}
                                size="sm"
                                colorScheme="blue"
                                aria-label="Find Part"
                                onClick={() => handleFindPart(line.Barcode)}
                              />
                            )}
                          </Td>
                          <Td fontSize="sm">{line.ProductName}</Td>
                          <Td fontSize="sm">{line.ASIN}</Td>
                          <Td fontSize="sm">{line.Barcode || "-"}</Td>
                          <Td isNumeric fontSize="sm">${Number(line.AmzPrice || 0).toFixed(2)}</Td>
                          <Td isNumeric fontSize="sm">${Number(line.DkaPrice || 0).toFixed(2)}</Td>
                          <Td isNumeric fontSize="sm">{line.AmzQuantity || 0}</Td>
                          <Td isNumeric fontSize="sm">{line.DkaQuantity || 0}</Td>
                          <Td isNumeric fontSize="sm">{line.SOH || 0}</Td>

                        </Tr>
                      );
                    })
                  ) : (
                    <Tr>
                      <Td colSpan={8} textAlign="center" py={4} color="gray.500">
                        No items found.
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>

            {/* 🔹 Approve / Reject Buttons */}
            <Flex justify="flex-end" mt={4} gap={4}>
              {hasAnyPriceIssue || hasAnyQtyMismatch ? (
                <Button colorScheme="red" onClick={() => handleOrderAction("Reject")}>
                  Reject
                </Button>
              ) : (
                <Button colorScheme="green" onClick={() => handleOrderAction("Approve")}>
                  Approve
                </Button>
              )}
            </Flex>
          </VStack>
        )}
      </Box>

      {/* Modal */}
       <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Available Parts for Barcode: {selectedBarcode}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
          {modalData.length > 0 ? (
  modalData.map((item, i) => (
    <Box key={i} p={3} borderWidth="1px" borderRadius="md" mb={2}>
      <Text><b>Part No:</b> {item.PartNo}</Text>
      <Text><b>Name:</b> {item.Description}</Text>
      <Text><b>Barcode:</b> {item.Barcode}</Text>
      <Text><b>SOH:</b> {item.SOH}</Text>

      {item.SOH === 0 && (
        <Button
          colorScheme="red"
          mt={2}
          onClick={async () => {
            try {
              const token = localStorage.getItem("accessToken");
              await axios.post(
                `${API_URL}/action-remove-barcode`,
                { PartNo: item.PartNo, Action: "RemoveBarcode" },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              alert(`Barcode for ${item.PartNo} removed successfully`);
              onClose();
            } catch (err) {
              console.error("Failed to remove barcode:", err);
              alert("Failed to remove barcode");
            }
          }}
        >
          Remove Barcode
        </Button>
      )}
    </Box>
  ))
) : (
  <Text color="gray.500">No parts found for this barcode.</Text>
)}

          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
